import React, { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Plus, Search, X, Car, Upload, Camera, Eye, AlertTriangle, Trash2,
  CheckCircle2, XCircle, FileSpreadsheet, Download, Clock, QrCode,
} from "lucide-react";
import { toast } from "sonner";
import QrScanner from "@/components/QrScanner";
import ExportExcelButton from "@/components/ExportExcelButton";

const VEHICLE_PARTS = [
  "Bonnet / Hood",
  "Front Bumper",
  "Front Fenders (Left & Right)",
  "Front Doors (Left & Right)",
  "Rear Doors (Left & Right)",
  "A-Pillars & Roof Trim (Left & Right)",
  "Rocker Panels / Side Skirts (Left & Right)",
  "Rear Quarter Panels (Left & Right)",
  "Rear Bumper",
  "Boot Lid / Tailgate",
  "Main Roof Panel",
  "Roof Spoiler",
  "Side Mirror Caps (Left & Right)",
  "B-Pillars & C-Pillars",
  "Door Cups / Cavities",
];

const STATUS_PILL = {
  pending_review: { bg: "#F59E0B", label: "Pending Review", icon: Clock },
  approved: { bg: "#10B981", label: "Approved", icon: CheckCircle2 },
  rejected: { bg: "#EF4444", label: "Rejected", icon: XCircle },
};

export default function Installations() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(user?.role === "manufacturer" ? "pending_review" : "");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const qs = [];
      if (q) qs.push(`q=${encodeURIComponent(q)}`);
      if (status) qs.push(`status=${status}`);
      const r = await api.get(`/installations${qs.length ? "?" + qs.join("&") : ""}`);
      setItems(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]);

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/installations/${id}`);
      setDetail(r.data);
    } catch { toast.error("Failed to load details"); }
  };

  const approveOrReject = async (id, approve, notes = "") => {
    try {
      const r = await api.post(`/installations/${id}/approve`, { approve, notes });
      toast.success(approve ? `Approved — warranty ${r.data.warranty_number}` : "Rejected");
      setDetail(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Action failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#64748B]">Operations</div>
          <h1 className="text-3xl tracking-tighter font-medium mt-1">Installations</h1>
        </div>
        <div className="flex gap-2">
          <ExportExcelButton path="/export/installations" filename="installations.xlsx"
            testid="export-installations-btn" />
          {(user?.role === "manufacturer" || user?.role === "distributor") && (
            <button data-testid="bulk-upload-btn" onClick={() => setBulkOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFFFFF] border border-[#E5E7EB] hover:bg-[#F8FAFC] rounded-md text-sm">
              <Upload size={14} /> Bulk Upload
            </button>
          )}
          {user?.role === "dealer" && (
            <button data-testid="new-installation-btn" onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#E63946] hover:bg-[#D32F2F] rounded-md text-sm">
              <Plus size={14} /> New Installation
            </button>
          )}
          {user?.role === "manufacturer" && (
            <button data-testid="mfr-new-installation-btn" onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#E63946] hover:bg-[#D32F2F] text-white rounded-md text-sm">
              <Plus size={14} /> New Installation
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <input data-testid="installation-search" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by customer, vehicle, PPF code..."
            className="w-full pl-9 pr-3 py-2 text-sm bg-[#FFFFFF] border border-[#E5E7EB] rounded-md text-slate-900 focus:outline-none focus:border-[#E63946]" />
        </div>
        <select data-testid="install-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
          className="px-3 py-2 text-sm bg-[#FFFFFF] border border-[#E5E7EB] rounded-md text-slate-900">
          <option value="">All statuses</option>
          <option value="pending_review">Pending Review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {loading ? <div className="text-sm text-[#475569]">Loading…</div> :
        items.length === 0 ? (
          <div className="text-center py-16 text-[#64748B] border border-dashed border-[#E5E7EB] rounded-xl">
            <Car size={28} className="mx-auto mb-3" /> No installations {status ? `with status ${status}` : "yet"}.
          </div>
        ) : (
          <div className="rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] overflow-hidden">
            <table className="w-full text-sm" data-testid="installations-table">
              <thead><tr className="border-b border-[#E5E7EB] text-left">
                {["Customer", "Vehicle", "Product", "SqFt", "Rolls", "Installer", "Status", "Installed", ""].map(h =>
                  <th key={h} className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold">{h}</th>)}
              </tr></thead>
              <tbody>{items.map((i) => (
                <tr key={i.id} className="border-b border-[#E5E7EB] hover:bg-[#F8FAFC]">
                  <td className="px-4 py-3">
                    <div className="font-medium">{i.customer_name}</div>
                    <div className="text-[11px] text-[#64748B] font-mono">+91 {i.customer_phone}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-mono text-[#E63946]">{i.vehicle_reg_no}</div>
                    <div className="text-[11px] text-[#64748B]">{i.vehicle_brand} {i.vehicle_model}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {i.product_name}
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#64748B] mt-0.5">{i.installation_type}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{i.sqft_required}</td>
                  <td className="px-4 py-3 text-xs">{(i.ppf_usages || []).length || 1}</td>
                  <td className="px-4 py-3 text-xs">{i.installer_name || "-"}</td>
                  <td className="px-4 py-3"><StatusPill status={i.status} /></td>
                  <td className="px-4 py-3 text-xs text-[#64748B]">{fmt(i.installed_at)}</td>
                  <td className="px-4 py-3">
                    <button data-testid={`view-install-${i.id}`} onClick={() => openDetail(i.id)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[#E5E7EB] rounded hover:bg-[#E5E7EB]">
                      <Eye size={12} /> View
                    </button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}

      {open && <NewInstallationModal onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
      {bulkOpen && <BulkUploadModal onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); load(); }} />}
      {detail && (
        <InstallationDetailModal
          data={detail}
          canApprove={user?.role === "manufacturer" && detail.status === "pending_review"}
          onApprove={(approve, notes) => approveOrReject(detail.id, approve, notes)}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}
function fmt(d) { if (!d) return "-"; try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return d; } }

function StatusPill({ status }) {
  const c = STATUS_PILL[status] || { bg: "#64748B", label: status || "-", icon: Clock };
  const Icon = c.icon;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.18em]"
      style={{ background: c.bg + "22", color: c.bg, border: `1px solid ${c.bg}55` }}>
      <Icon size={10} /> {c.label}
    </span>
  );
}

function InstallationDetailModal({ data, canApprove, onApprove, onClose }) {
  const [notes, setNotes] = useState("");
  const [imgIndex, setImgIndex] = useState(0);
  const images = data.installation_images || [];

  const downloadPdf = () => {
    if (!data.warranty_id) return;
    const token = localStorage.getItem("cpl_token");
    fetch(`${API_BASE}/warranties/${data.warranty_id}/certificate.pdf`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.blob()).then(b => {
      const url = URL.createObjectURL(b);
      const a = document.createElement("a"); a.href = url; a.download = `warranty_${data.warranty_id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between sticky top-0 bg-[#F8FAFC] z-10">
          <div>
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-medium">Installation Details</h3>
              <StatusPill status={data.status} />
            </div>
            <div className="text-xs text-[#64748B] font-mono mt-0.5">{data.warranty?.warranty_number || (data.status === "pending_review" ? "Warranty pending approval" : "—")}</div>
          </div>
          <button onClick={onClose} data-testid="detail-close" className="text-[#475569] hover:text-slate-900"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5 text-sm">
          {images.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B] mb-2">Installation Images ({images.length})</div>
              <div className="rounded-lg border border-[#E5E7EB] overflow-hidden">
                <div className="aspect-video bg-[#FFFFFF] flex items-center justify-center">
                  <img src={images[imgIndex]} alt="install" className="max-h-full max-w-full object-contain" />
                </div>
                <div className="p-2 bg-[#FFFFFF] flex gap-2 overflow-x-auto">
                  {images.map((im, i) => (
                    <button key={i} onClick={() => setImgIndex(i)} data-testid={`img-thumb-${i}`}
                      className={`h-16 w-16 flex-shrink-0 rounded overflow-hidden border-2 ${i === imgIndex ? "border-[#E63946]" : "border-transparent"}`}>
                      <img src={im} className="w-full h-full object-cover" alt="" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <KVGroup title="Customer" rows={[
            ["Name", data.customer_name], ["Phone", data.customer_phone], ["Email", data.customer_email || "-"],
            ["Address", [data.customer_address_street, data.customer_address_city, data.customer_address_state, data.customer_address_pin].filter(Boolean).join(", ") || "-"],
          ]} />
          <KVGroup title="Vehicle" rows={[
            ["Reg No.", data.vehicle_reg_no],
            ["Brand / Model", `${data.vehicle_brand || ""} ${data.vehicle_model || ""}`.trim()],
            ["Color", data.vehicle_color || "-"], ["Mfg Year", data.vehicle_manuf_year || "-"],
            ["Registration Year", data.vehicle_registration_year || "-"],
            ["Registration Expiry", data.vehicle_registration_expiry || "-"],
          ]} />
          <KVGroup title="Product & Coverage" rows={[
            ["Product", data.product_name], ["Type", data.installation_type],
            ["Total SqFt Applied", `${data.sqft_required} SqFt`],
          ]} />
          {data.parts_covered && data.parts_covered.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B] mb-2">Parts Covered ({data.parts_covered.length})</div>
              <div className="flex flex-wrap gap-1.5">
                {data.parts_covered.map((p, i) => (
                  <span key={i} className="text-[10px] px-2 py-1 rounded bg-[#E63946]/10 text-[#E63946] border border-[#E63946]/30">{p}</span>
                ))}
              </div>
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B] mb-2">PPF Rolls Used</div>
            <div className="rounded-md border border-[#E5E7EB] overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-[#FFFFFF]"><tr>
                  {["PPF Code", "Roll SqFt", "Used SqFt"].map(h => <th key={h} className="px-3 py-2 text-left text-[10px] uppercase text-[#64748B]">{h}</th>)}
                </tr></thead>
                <tbody>{(data.ppf_usages || []).map((u, i) => (
                  <tr key={i} className="border-t border-[#E5E7EB]">
                    <td className="px-3 py-2 font-mono text-[#E63946]">{u.ppf_code}</td>
                    <td className="px-3 py-2 font-mono">{u.roll_size_sqft}</td>
                    <td className="px-3 py-2 font-mono font-bold">{u.sqft_used}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
          <KVGroup title="Dealer" rows={[
            ["Dealer Name", data.dealer?.name], ["Phone", data.dealer?.phone],
            ["Email", data.dealer?.email], ["City", data.dealer?.address_city],
          ]} />
          <KVGroup title="Installer" rows={[
            ["Installer Name", data.installer_name], ["Phone", data.installer_phone],
            ["Email", data.installer_email || "-"],
          ]} />
          {data.review_notes && (
            <div className="rounded-md p-3 bg-[#FFFFFF] border border-[#E5E7EB]">
              <div className="text-[10px] uppercase text-[#64748B]">Review Notes</div>
              <div className="text-xs text-slate-900 mt-1">{data.review_notes}</div>
            </div>
          )}

          {canApprove && (
            <div className="rounded-md p-4 bg-[#FFFFFF] border border-[#E5E7EB]">
              <div className="text-[10px] uppercase text-[#64748B] mb-2 font-bold">Review Decision</div>
              <textarea
                data-testid="review-notes"
                value={notes} onChange={(e) => setNotes(e.target.value)}
                rows={2} placeholder="Optional review notes..."
                className="w-full px-3 py-2 bg-[#FFFFFF] border border-[#E5E7EB] rounded text-sm text-slate-900 resize-y focus:outline-none focus:border-[#E63946]" />
              <div className="mt-3 flex gap-2 justify-end">
                <button data-testid="reject-install-btn" onClick={() => onApprove(false, notes)}
                  className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/30 rounded-md hover:bg-[#EF4444]/20">
                  <XCircle size={14} /> Reject
                </button>
                <button data-testid="approve-install-btn" onClick={() => onApprove(true, notes)}
                  className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30 rounded-md hover:bg-[#10B981]/25">
                  <CheckCircle2 size={14} /> Approve &amp; Generate Warranty
                </button>
              </div>
            </div>
          )}

          {data.warranty_id && (
            <div className="flex justify-end">
              <button data-testid="detail-download-pdf" onClick={downloadPdf}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#E63946]/10 border border-[#E63946]/30 text-[#E63946] hover:bg-[#E63946]/20 rounded-md">
                <Download size={14} /> Download Warranty PDF
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KVGroup({ title, rows }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B] mb-2">{title}</div>
      <div className="grid grid-cols-2 gap-3 text-xs">
        {rows.map(([k, v], i) => (
          <div key={i}>
            <div className="text-[10px] text-[#64748B]">{k}</div>
            <div className="text-slate-900 mt-0.5">{v || "-"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ----- BULK UPLOAD MODAL -----
function BulkUploadModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadSample = () => {
    const token = localStorage.getItem("cpl_token");
    fetch(`${API_BASE}/bulk/installations-sample-csv`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.blob()).then(b => {
      const url = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = url; a.download = "bulk_installations_sample.csv"; a.click();
      URL.revokeObjectURL(url);
    });
  };

  const submit = async () => {
    if (!file) { toast.error("Choose a file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/bulk/installations-upload", fd);
      setResult(r.data);
      toast.success(`Inserted ${r.data.inserted} • Failed ${r.data.failed}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
          <h3 className="text-lg font-medium">Bulk Upload Existing Installations</h3>
          <button onClick={onClose} className="text-[#475569] hover:text-slate-900" data-testid="bulk-modal-close"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4 text-sm">
          <p className="text-[#475569]">
            Import past installations in bulk. Each row auto-creates a PPF code, approved installation and warranty. Vehicles must exist in the RTO database and dealer mobile must belong to your network.
          </p>
          <div>
            <button data-testid="download-sample" onClick={downloadSample}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#FFFFFF] border border-[#E5E7EB] hover:bg-[#F8FAFC] rounded-md">
              <Download size={14} /> Download sample CSV
            </button>
            <div className="text-[10px] text-[#64748B] mt-2 font-mono break-all">
              dealer_mobile, product_name, sqft_used, vehicle_reg_no, customer_name, customer_phone, customer_email, installer_name, installer_phone, installer_email, installation_type
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">CSV File</label>
            <input type="file" accept=".csv,text/csv" data-testid="bulk-file" onChange={(e) => setFile(e.target.files?.[0])}
              className="mt-1 block text-sm text-[#475569] file:mr-3 file:px-3 file:py-1.5 file:rounded file:bg-[#FFFFFF] file:text-slate-900 file:border file:border-[#E5E7EB] file:cursor-pointer" />
          </div>
          {result && (
            <div className="rounded-md p-3 bg-[#FFFFFF] border border-[#E5E7EB]">
              <div className="text-xs"><span className="text-[#10B981]">✓ {result.inserted} inserted</span> <span className="text-[#EF4444] ml-3">✗ {result.failed} failed</span></div>
              {result.errors?.length > 0 && (
                <div className="mt-2 max-h-40 overflow-auto space-y-1">
                  {result.errors.map((e, i) => <div key={i} className="text-[10px] font-mono text-[#EF4444]">Row {e.row}: {e.error}</div>)}
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button onClick={onDone} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-md hover:bg-[#E5E7EB]">Done</button>
            <button data-testid="bulk-upload-submit" onClick={submit} disabled={busy || !file}
              className="px-5 py-2 bg-[#E63946] hover:bg-[#D32F2F] rounded-md text-sm font-medium disabled:opacity-50">
              {busy ? "Uploading..." : "Upload"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ----- NEW INSTALLATION MODAL -----
function NewInstallationModal({ onClose, onDone }) {
  const { user } = useAuth();
  const isManufacturer = user?.role === "manufacturer";
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    installation_type: "full", sqft_required: 100,
    installation_date: new Date().toISOString().slice(0, 10),
    customer_name: "", customer_phone: "", customer_email: "",
    customer_address_street: "", customer_address_city: "", customer_address_state: "", customer_address_pin: "",
    installer_name: "", installer_phone: "", installer_email: "",
    vehicle_reg_no: "",
  });
  const [vehicle, setVehicle] = useState(null);
  const [vBusy, setVBusy] = useState(false);
  const [vCountdown, setVCountdown] = useState(0);
  const [rolls, setRolls] = useState([]);
  const [selectedUsages, setSelectedUsages] = useState([]);
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [partsCovered, setPartsCovered] = useState([]);
  const [ppfQuery, setPpfQuery] = useState("");
  const [selectedDealerId, setSelectedDealerId] = useState("");
  const [dealers, setDealers] = useState([]);
  const [manualMode, setManualMode] = useState(false);
  const [manualVehicle, setManualVehicle] = useState({
    reg_no: "", brand: "", model: "", color: "", manuf_year: "",
    fuel_type: "", seating_capacity: "", registration_year: "",
    registration_date: "", registration_expiry: "", chassis_no: "",
  });
  const togglePart = (p) => setPartsCovered(partsCovered.includes(p) ? partsCovered.filter(x => x !== p) : [...partsCovered, p]);

  const totalSelected = selectedUsages.reduce((s, u) => s + Number(u.sqft_used || 0), 0);
  const remainingNeeded = Number(form.sqft_required) - totalSelected;

  const fetchVehicle = async () => {
    if (!form.vehicle_reg_no) return;
    if (vCountdown > 0) {
      toast.error(`Please wait ${vCountdown}s — RTO API allows 1 request/minute`);
      return;
    }
    setVBusy(true);
    try {
      const r = await api.get(`/vehicles/lookup?reg_no=${encodeURIComponent(form.vehicle_reg_no)}`);
      // Only start throttle after a successful hit against the rate-limited API
      setVCountdown(60);
      const timer = setInterval(() => {
        setVCountdown((c) => {
          if (c <= 1) { clearInterval(timer); return 0; }
          return c - 1;
        });
      }, 1000);
      if (r.data.registration_expired) {
        toast.error(`Vehicle registration expired on ${r.data.registration_expiry}. Cannot apply PPF.`);
        setVehicle(null);
      } else {
        setVehicle(r.data);
        toast.success("Vehicle auto-filled from RTO data");
      }
    } catch { setVehicle(null); toast.error("Vehicle not found"); }
    finally { setVBusy(false); }
  };

  const loadRolls = async () => {
    if (!form.sqft_required || form.sqft_required <= 0) return;
    try {
      let url;
      if (isManufacturer) {
        if (!selectedDealerId) { setRolls([]); return; }
        url = `/stock/available-rolls-for-dealer?dealer_user_id=${selectedDealerId}`;
      } else {
        url = form.installation_type === "full"
          ? `/stock/available-rolls?required_sqft=${form.sqft_required}`
          : `/stock/available-partial`;
      }
      const r = await api.get(url);
      setRolls(r.data);
    } catch { setRolls([]); }
  };

  useEffect(() => {
    if (isManufacturer) {
      api.get("/dealers").then(r => setDealers(r.data || [])).catch(() => setDealers([]));
    }
  }, [isManufacturer]);

  const onScanRoll = async (code) => {
    setScanOpen(false);
    // Look up in current available rolls; if found, auto-add
    const existing = rolls.find(r => r.ppf_code === code);
    if (existing) {
      addRollUsage(existing);
      toast.success(`Scanned & added: ${code}`);
      return;
    }
    // Otherwise fetch from API to see if it exists
    try {
      const r = await api.get(`/stock/items?q=${encodeURIComponent(code)}`);
      const match = (r.data || []).find(x => x.ppf_code === code);
      if (match && (match.state === "allocated" || match.state === "partially_used") && match.current_owner_role === "dealer") {
        addRollUsage(match);
        toast.success(`Scanned: ${code}`);
      } else {
        toast.error("Scanned roll is not in your allocated stock");
      }
    } catch { toast.error("Roll not found"); }
  };

  const addRollUsage = (roll) => {
    if (selectedUsages.find(u => u.ppf_code === roll.ppf_code)) { toast.error("Roll already added"); return; }
    const sqft = Math.min(remainingNeeded, roll.remaining_sqft);
    setSelectedUsages([...selectedUsages, {
      ppf_code: roll.ppf_code, sqft_used: sqft, roll_size_sqft: roll.roll_size_sqft,
      remaining_sqft: roll.remaining_sqft, product_id: roll.product_id, product_name: roll.product_name,
    }]);
  };
  const updateSqft = (i, v) => {
    const next = [...selectedUsages]; next[i].sqft_used = Number(v); setSelectedUsages(next);
  };
  const removeUsage = (i) => setSelectedUsages(selectedUsages.filter((_, ix) => ix !== i));

  const onImage = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const processed = [];
    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) { toast.error(`${f.name} > 5MB skipped`); continue; }
      const b64 = await compressImage(f);
      processed.push(b64);
    }
    setImages([...images, ...processed].slice(0, 5));
  };

  const validateStep1 = () => {
    if (isManufacturer && !selectedDealerId) { toast.error("Select a dealer to record installation under"); return false; }
    if (!form.vehicle_reg_no && !(manualMode && manualVehicle.reg_no)) { toast.error("Enter vehicle registration"); return false; }
    if (!vehicle && !manualMode) { toast.error("Fetch vehicle or use 'Enter Manually'"); return false; }
    if (manualMode) {
      const req = ["reg_no", "brand", "model", "registration_expiry"];
      for (const k of req) if (!(manualVehicle[k] || "").toString().trim()) { toast.error(`Manual ${k.replace("_", " ")} required`); return false; }
    }
    if (form.sqft_required <= 0 || form.sqft_required > 5000) { toast.error("SqFt required 1..5000"); return false; }
    if (form.installation_type === "partial" && partsCovered.length === 0) {
      toast.error("Select at least one vehicle part for partial coverage"); return false;
    }
    return true;
  };
  const validateStep2 = () => {
    if (selectedUsages.length === 0) { toast.error("Add at least one PPF roll"); return false; }
    if (totalSelected !== Number(form.sqft_required)) { toast.error(`Total SqFt (${totalSelected}) must equal required (${form.sqft_required})`); return false; }
    if (new Set(selectedUsages.map(u => u.product_id)).size > 1) { toast.error("All rolls must be same product"); return false; }
    return true;
  };
  const validateStep3 = () => {
    if (!form.customer_name || !form.customer_phone || !form.customer_email) { toast.error("Customer name, phone and email are mandatory"); return false; }
    if (!form.installer_name || !form.installer_phone) { toast.error("Installer name and phone are mandatory"); return false; }
    if (images.length === 0) { toast.error("Upload at least one installation image"); return false; }
    return true;
  };

  const submit = async () => {
    if (!validateStep3()) return;
    setBusy(true);
    try {
      const payload = {
        installation_type: form.installation_type,
        sqft_required: Number(form.sqft_required),
        ppf_usages: selectedUsages.map(u => ({ ppf_code: u.ppf_code, sqft_used: Number(u.sqft_used) })),
        parts_covered: form.installation_type === "partial" ? partsCovered : [],
        customer_name: form.customer_name, customer_phone: form.customer_phone, customer_email: form.customer_email,
        customer_address_street: form.customer_address_street, customer_address_city: form.customer_address_city,
        customer_address_state: form.customer_address_state, customer_address_pin: form.customer_address_pin,
        installer_name: form.installer_name, installer_phone: form.installer_phone, installer_email: form.installer_email,
        vehicle_reg_no: form.vehicle_reg_no,
        installation_date: form.installation_date,
        installation_images: images,
      };
      if (isManufacturer && selectedDealerId) payload.dealer_user_id = selectedDealerId;
      if (manualMode) {
        payload.manual_vehicle = {
          ...manualVehicle,
          reg_no: (manualVehicle.reg_no || form.vehicle_reg_no || "").toUpperCase().replace(/\s+/g, ""),
          seating_capacity: manualVehicle.seating_capacity ? Number(manualVehicle.seating_capacity) : null,
          manuf_year: manualVehicle.manuf_year ? Number(manualVehicle.manuf_year) : null,
        };
        payload.vehicle_reg_no = payload.manual_vehicle.reg_no;
      }
      const r = await api.post("/installations", payload);
      toast.success(r.data.message || "Submitted for manufacturer review");
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between sticky top-0 bg-[#F8FAFC] z-10">
          <div>
            <h3 className="text-lg font-medium">New Installation</h3>
            <div className="text-[11px] text-[#64748B] mt-0.5">Step {step} of 3 · {["Vehicle & Coverage", "Select PPF Rolls", "Customer & Installer"][step - 1]}</div>
          </div>
          <button onClick={onClose} className="text-[#475569] hover:text-slate-900" data-testid="install-modal-close"><X size={18} /></button>
        </div>
        <div className="px-6 pt-4">
          <div className="flex gap-1">
            {[1, 2, 3].map(s => <div key={s} className={`h-1 flex-1 rounded ${s <= step ? "bg-[#E63946]" : "bg-[#E5E7EB]"}`} />)}
          </div>
        </div>
        <div className="p-6 space-y-6">
          {step === 1 && (
            <>
              {isManufacturer && (
                <Section title="Distributor / Dealer">
                  <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Select Dealer</label>
                  <select data-testid="mfr-select-dealer"
                    value={selectedDealerId} onChange={(e) => setSelectedDealerId(e.target.value)}
                    className="mt-1 w-full px-3 py-2.5 bg-white border border-[#E5E7EB] rounded-md text-slate-900 focus:outline-none focus:border-[#E63946]">
                    <option value="">— Choose dealer —</option>
                    {dealers.map(d => (
                      <option key={d.user_id} value={d.user_id}>{d.name} ({d.address_city})</option>
                    ))}
                  </select>
                  <div className="text-[10px] text-[#64748B] mt-1">
                    Distributor is auto-derived from the dealer record.
                  </div>
                </Section>
              )}
              <Section title="Vehicle">
                <div className="flex gap-2">
                  <input data-testid="install-vehicle-reg" placeholder="GJ01AB1234"
                    value={form.vehicle_reg_no} onChange={(e) => setForm({ ...form, vehicle_reg_no: e.target.value.toUpperCase() })}
                    className="flex-1 px-3 py-2.5 bg-[#FFFFFF] border border-[#E5E7EB] rounded-md font-mono text-slate-900 focus:outline-none focus:border-[#E63946]" />
                  <button type="button" data-testid="install-fetch-vehicle" onClick={fetchVehicle} disabled={vBusy || vCountdown > 0}
                    className="px-4 py-2 text-sm bg-[#FFFFFF] border border-[#E5E7EB] hover:bg-[#F8FAFC] rounded-md disabled:opacity-50 whitespace-nowrap">
                    {vBusy ? "Fetching..." : vCountdown > 0 ? `Wait ${vCountdown}s` : "Fetch RTO"}
                  </button>
                  {!manualMode ? (
                    <button type="button" data-testid="install-enter-manually" onClick={() => {
                      setManualMode(true);
                      setVehicle(null);
                      setManualVehicle(mv => ({ ...mv, reg_no: form.vehicle_reg_no || mv.reg_no }));
                    }}
                      className="px-4 py-2 text-sm bg-[#FFFFFF] border border-[#E63946]/40 text-[#E63946] hover:bg-[#E63946]/10 rounded-md whitespace-nowrap">
                      Enter Manually
                    </button>
                  ) : (
                    <button type="button" data-testid="install-cancel-manual" onClick={() => {
                      setManualMode(false);
                      setManualVehicle({
                        reg_no: "", brand: "", model: "", color: "", manuf_year: "",
                        fuel_type: "", seating_capacity: "", registration_year: "",
                        registration_date: "", registration_expiry: "", chassis_no: "",
                      });
                    }}
                      className="px-4 py-2 text-sm bg-[#FFFFFF] border border-[#E5E7EB] text-[#475569] hover:bg-[#F8FAFC] rounded-md whitespace-nowrap">
                      Cancel Manual
                    </button>
                  )}
                </div>
                {manualMode && (
                  <div className="mt-3 rounded-md border border-[#E63946]/30 bg-[#E63946]/5 p-4" data-testid="install-manual-form">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#E63946] bg-[#E63946]/10 border border-[#E63946]/30 rounded px-2 py-0.5">Manual Entry Mode</span>
                      <span className="text-[11px] text-[#64748B]">RTO fetch disabled — enter details manually</span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Field label="Reg No *" tid="manual-reg-no"
                        value={manualVehicle.reg_no}
                        onChange={(v) => setManualVehicle({ ...manualVehicle, reg_no: v.toUpperCase().replace(/\s+/g, "") })} />
                      <Field label="Brand *" tid="manual-brand"
                        value={manualVehicle.brand}
                        onChange={(v) => setManualVehicle({ ...manualVehicle, brand: v })} />
                      <Field label="Model *" tid="manual-model"
                        value={manualVehicle.model}
                        onChange={(v) => setManualVehicle({ ...manualVehicle, model: v })} />
                      <Field label="Color" tid="manual-color"
                        value={manualVehicle.color}
                        onChange={(v) => setManualVehicle({ ...manualVehicle, color: v })} />
                      <Field label="Fuel Type" tid="manual-fuel"
                        value={manualVehicle.fuel_type}
                        onChange={(v) => setManualVehicle({ ...manualVehicle, fuel_type: v })} />
                      <Field label="Seating Capacity" tid="manual-seats"
                        value={manualVehicle.seating_capacity}
                        onChange={(v) => setManualVehicle({ ...manualVehicle, seating_capacity: v })} />
                      <Field label="Mfg Year" tid="manual-manuf-year"
                        value={manualVehicle.manuf_year}
                        onChange={(v) => setManualVehicle({ ...manualVehicle, manuf_year: v })} />
                      <Field label="Registration Year" tid="manual-reg-year"
                        value={manualVehicle.registration_year}
                        onChange={(v) => setManualVehicle({ ...manualVehicle, registration_year: v })} />
                      <div>
                        <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#64748B]">Registration Date</label>
                        <input type="date" data-testid="manual-reg-date"
                          value={manualVehicle.registration_date}
                          onChange={(e) => setManualVehicle({ ...manualVehicle, registration_date: e.target.value })}
                          className="mt-1 w-full px-3 py-2 bg-white border border-[#E5E7EB] rounded-md text-slate-900 text-sm focus:outline-none focus:border-[#E63946]" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#64748B]">Registration Expiry *</label>
                        <input type="date" data-testid="manual-reg-expiry"
                          value={manualVehicle.registration_expiry}
                          onChange={(e) => setManualVehicle({ ...manualVehicle, registration_expiry: e.target.value })}
                          className="mt-1 w-full px-3 py-2 bg-white border border-[#E5E7EB] rounded-md text-slate-900 text-sm focus:outline-none focus:border-[#E63946]" />
                      </div>
                      <div className="sm:col-span-2">
                        <Field label="Chassis No" tid="manual-chassis"
                          value={manualVehicle.chassis_no}
                          onChange={(v) => setManualVehicle({ ...manualVehicle, chassis_no: v })} />
                      </div>
                    </div>
                    <div className="text-[10px] text-[#64748B] mt-2">Fields marked * are required. Warranty coverage will be capped at the registration expiry date.</div>
                  </div>
                )}
                {vCountdown > 0 && (
                  <div className="mt-2 text-[11px] text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded px-3 py-2" data-testid="rto-throttle-msg">
                    RTO API allows only 1 request per minute. Next fetch available in <b>{vCountdown}s</b>.
                  </div>
                )}
                {vehicle && (
                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs bg-[#FFFFFF] border border-[#E5E7EB] rounded p-3" data-testid="install-vehicle-card">
                    <KV k="Vehicle No." v={vehicle.reg_no} mono />
                    <KV k="Registration Date" v={vehicle.registration_date || vehicle.registration_year} />
                    <KV k="Brand" v={vehicle.brand} /> <KV k="Model" v={vehicle.model} />
                    <KV k="Fuel Type" v={vehicle.fuel_type} />
                    <KV k="Seating Capacity" v={vehicle.seating_capacity} />
                    <KV k="Color" v={vehicle.color} /> <KV k="Mfg Year" v={vehicle.manuf_year} />
                    <KV k="Reg. Expiry" v={vehicle.registration_expiry} highlight />
                    <div className="col-span-2"><KV k="Chassis" v={vehicle.chassis_no} mono /></div>
                    {vehicle.days_to_registration_expiry !== null && vehicle.days_to_registration_expiry < 365 && (
                      <div className="col-span-2 text-xs text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded p-2 inline-flex items-center gap-2">
                        <AlertTriangle size={12} /> Only {vehicle.days_to_registration_expiry} days until registration expires — warranty will be capped.
                      </div>
                    )}
                  </div>
                )}
              </Section>
              <Section title="Coverage">
                <div className="flex gap-2 mb-3">
                  {["full", "partial"].map((t) => (
                    <button key={t} type="button" data-testid={`install-type-${t}`}
                      onClick={() => setForm({ ...form, installation_type: t })}
                      className={`flex-1 py-3 rounded-md border text-sm capitalize transition-colors ${
                        form.installation_type === t ? "bg-[#E63946]/10 border-[#E63946] text-[#E63946]" : "bg-[#FFFFFF] border-[#E5E7EB] text-[#475569]"
                      }`}>{t} vehicle</button>
                  ))}
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Installation Date</label>
                  <input type="date"
                    data-testid="install-date"
                    value={form.installation_date}
                    min={new Date(Date.now() - 30*24*60*60*1000).toISOString().slice(0,10)}
                    max={new Date().toISOString().slice(0,10)}
                    onChange={(e) => setForm({ ...form, installation_date: e.target.value })}
                    className="mt-1 w-full px-3 py-2.5 bg-white border border-[#E5E7EB] rounded-md text-slate-900 focus:outline-none focus:border-[#E63946]" />
                  <div className="text-[10px] text-[#64748B] mt-1">Must be within the last 30 days.</div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Total PPF Required (SqFt)</label>
                  <input type="number" min={1} max={5000} data-testid="install-sqft-required"
                    value={form.sqft_required} onChange={(e) => setForm({ ...form, sqft_required: e.target.value })}
                    className="mt-1 w-full px-3 py-2.5 bg-[#FFFFFF] border border-[#E5E7EB] rounded-md font-mono text-slate-900 focus:outline-none focus:border-[#E63946]" />
                  <div className="text-[10px] text-[#64748B] mt-1">Standard roll is 300 SqFt. Use partial mode if less than a roll or spanning multiple rolls.</div>
                </div>
                {form.installation_type === "partial" && (
                  <div className="mt-4">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B] mb-2">
                      Vehicle Parts Covered <span className="text-[#E63946]">*</span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2 max-h-56 overflow-auto p-1 rounded-md bg-[#F8FAFC] border border-[#E5E7EB]">
                      {VEHICLE_PARTS.map((p) => (
                        <label key={p} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white cursor-pointer text-xs" data-testid={`part-${p.split(" ")[0].replace("/", "")}`}>
                          <input type="checkbox" className="accent-[#E63946]"
                            checked={partsCovered.includes(p)} onChange={() => togglePart(p)} />
                          <span>{p}</span>
                        </label>
                      ))}
                    </div>
                    <div className="text-[10px] text-[#64748B] mt-1">Selected: {partsCovered.length} part(s)</div>
                  </div>
                )}
              </Section>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-md hover:bg-[#E5E7EB]">Cancel</button>
                <button type="button" data-testid="install-next-1" onClick={() => { if (validateStep1()) { loadRolls(); setStep(2); } }}
                  className="px-5 py-2 bg-[#E63946] hover:bg-[#D32F2F] rounded-md text-sm font-medium">Next →</button>
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">Available PPF Rolls ({rolls.filter(r => !ppfQuery || (r.ppf_code || "").toLowerCase().includes(ppfQuery.toLowerCase()) || (r.product_name || "").toLowerCase().includes(ppfQuery.toLowerCase())).length})</div>
                <button type="button" data-testid="scan-qr-btn" onClick={() => setScanOpen(true)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-[#E63946]/10 border border-[#E63946]/30 text-[#E63946] hover:bg-[#E63946]/20 rounded-md">
                  <QrCode size={12} /> Scan Roll QR / Barcode
                </button>
              </div>
              <div className="relative mb-2">
                <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                <input
                  data-testid="ppf-search"
                  value={ppfQuery} onChange={(e) => setPpfQuery(e.target.value)}
                  placeholder="Search by PPF Code or product..."
                  className="w-full pl-9 pr-3 py-2 text-xs bg-white border border-[#E5E7EB] rounded-md font-mono focus:outline-none focus:border-[#E63946]"
                />
              </div>
              <div>
                <div className="rounded-md border border-[#E5E7EB] overflow-hidden">
                  <table className="w-full text-xs" data-testid="available-rolls-table">
                    <thead className="bg-[#FFFFFF]"><tr>
                      {["PPF Code", "Product", "Roll", "Remaining", "Vehicles", ""].map(h =>
                        <th key={h} className="px-3 py-2 text-left text-[10px] uppercase text-[#64748B]">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {rolls.length === 0 && (
                        <tr><td colSpan={6} className="text-center py-8 text-[#64748B]">No matching rolls in your stock.</td></tr>
                      )}
                      {rolls.filter(r => !ppfQuery || (r.ppf_code || "").toLowerCase().includes(ppfQuery.toLowerCase()) || (r.product_name || "").toLowerCase().includes(ppfQuery.toLowerCase())).map(r => {
                        const already = selectedUsages.find(u => u.ppf_code === r.ppf_code);
                        return (
                          <tr key={r.id} className="border-t border-[#E5E7EB] hover:bg-[#F8FAFC]">
                            <td className="px-3 py-2 font-mono text-[#E63946]">{r.ppf_code}</td>
                            <td className="px-3 py-2">{r.product_name}</td>
                            <td className="px-3 py-2 font-mono">{r.roll_size_sqft}</td>
                            <td className="px-3 py-2 font-mono font-bold text-[#10B981]">{r.remaining_sqft}</td>
                            <td className="px-3 py-2 text-[10px] text-[#64748B]">{(r.vehicles_used || []).length}/2</td>
                            <td className="px-3 py-2">
                              <button type="button" data-testid={`add-roll-${r.ppf_code}`} onClick={() => addRollUsage(r)} disabled={!!already}
                                className="text-xs px-2 py-1 border border-[#E5E7EB] rounded hover:bg-[#E5E7EB] disabled:opacity-40">
                                {already ? "Added" : "Add"}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <Section title="Selected Rolls">
                {selectedUsages.length === 0 ? (
                  <div className="text-xs text-[#64748B] py-4 text-center border border-dashed border-[#E5E7EB] rounded-md">No rolls added yet.</div>
                ) : (
                  <div className="space-y-2">
                    {selectedUsages.map((u, i) => (
                      <div key={i} className="flex items-center gap-3 rounded-md bg-[#FFFFFF] border border-[#E5E7EB] p-2" data-testid={`selected-usage-${i}`}>
                        <div className="flex-1">
                          <div className="font-mono text-[#E63946] text-xs">{u.ppf_code}</div>
                          <div className="text-[10px] text-[#64748B]">{u.product_name} · Roll {u.roll_size_sqft} SqFt · {u.remaining_sqft} SqFt remaining</div>
                        </div>
                        <input type="number" min={1} max={u.remaining_sqft} data-testid={`usage-sqft-${i}`}
                          value={u.sqft_used} onChange={(e) => updateSqft(i, e.target.value)}
                          className="w-24 px-2 py-1 bg-[#FFFFFF] border border-[#E5E7EB] rounded font-mono text-sm text-slate-900" />
                        <span className="text-xs text-[#475569]">SqFt</span>
                        <button type="button" onClick={() => removeUsage(i)} data-testid={`remove-usage-${i}`}
                          className="p-2 text-[#EF4444] hover:bg-[#EF4444]/10 rounded"><Trash2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <Balance k="Required" v={form.sqft_required + " SqFt"} />
                  <Balance k="Selected" v={totalSelected + " SqFt"} color="#10B981" />
                  <Balance k="Balance" v={remainingNeeded + " SqFt"} color={remainingNeeded === 0 ? "#10B981" : "#F59E0B"} />
                </div>
              </Section>
              <div className="flex justify-between gap-3 pt-2">
                <button type="button" onClick={() => setStep(1)} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-md hover:bg-[#E5E7EB]">← Back</button>
                <button type="button" data-testid="install-next-2" onClick={() => { if (validateStep2()) setStep(3); }}
                  className="px-5 py-2 bg-[#E63946] hover:bg-[#D32F2F] rounded-md text-sm font-medium">Next →</button>
              </div>
            </>
          )}
          {step === 3 && (
            <>
              <Section title="Customer (all fields required)">
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Name *" tid="install-customer-name" value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} />
                  <Field label="Phone *" tid="install-customer-phone" value={form.customer_phone} onChange={(v) => setForm({ ...form, customer_phone: v })} />
                  <Field label="Email *" tid="install-customer-email" value={form.customer_email} onChange={(v) => setForm({ ...form, customer_email: v })} />
                  <Field label="PIN Code" tid="install-customer-pin" value={form.customer_address_pin} onChange={(v) => setForm({ ...form, customer_address_pin: v })} />
                  <div className="sm:col-span-2"><Field label="Street" tid="install-customer-street" value={form.customer_address_street} onChange={(v) => setForm({ ...form, customer_address_street: v })} /></div>
                  <Field label="City" tid="install-customer-city" value={form.customer_address_city} onChange={(v) => setForm({ ...form, customer_address_city: v })} />
                  <Field label="State" tid="install-customer-state" value={form.customer_address_state} onChange={(v) => setForm({ ...form, customer_address_state: v })} />
                </div>
              </Section>
              <Section title="Installer (person who applied the PPF)">
                <div className="grid sm:grid-cols-3 gap-3">
                  <Field label="Name *" tid="install-installer-name" value={form.installer_name} onChange={(v) => setForm({ ...form, installer_name: v })} />
                  <Field label="Phone *" tid="install-installer-phone" value={form.installer_phone} onChange={(v) => setForm({ ...form, installer_phone: v })} />
                  <Field label="Email" tid="install-installer-email" value={form.installer_email} onChange={(v) => setForm({ ...form, installer_email: v })} />
                </div>
              </Section>
              <Section title="Installation Images (up to 5)">
                <div className="grid grid-cols-5 gap-2">
                  {[0, 1, 2, 3, 4].map(i => (
                    <div key={i} className="aspect-square rounded-md border border-dashed border-[#E5E7EB] bg-[#FFFFFF] flex items-center justify-center overflow-hidden">
                      {images[i] ? <img src={images[i]} alt={`img-${i}`} className="w-full h-full object-cover" /> : <Camera size={20} className="text-[#64748B]" />}
                    </div>
                  ))}
                </div>
                <label className="mt-3 inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#FFFFFF] border border-[#E5E7EB] hover:bg-[#F8FAFC] rounded-md cursor-pointer">
                  <Upload size={14} /> Upload Images
                  <input data-testid="install-images-input" type="file" accept="image/*" multiple onChange={onImage} className="hidden" />
                </label>
              </Section>
              <div className="rounded-md p-3 bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-[#F59E0B] text-xs">
                Your installation will be submitted for manufacturer review. The customer will receive a WhatsApp confirmation that their warranty is under review; the certificate is generated only after approval.
              </div>
              <div className="flex justify-between gap-3 pt-2">
                <button type="button" onClick={() => setStep(2)} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-md hover:bg-[#E5E7EB]">← Back</button>
                <button type="button" data-testid="install-submit" onClick={submit} disabled={busy}
                  className="px-6 py-2.5 bg-[#E63946] hover:bg-[#D32F2F] rounded-md text-sm font-medium disabled:opacity-50">
                  {busy ? "Submitting..." : "Submit for Review"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      {scanOpen && <QrScanner onScan={onScanRoll} onClose={() => setScanOpen(false)} />}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B] mb-2">{title}</div>
      {children}
    </div>
  );
}
function Field({ label, value, onChange, tid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#64748B]">{label}</label>
      <input data-testid={tid} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2 bg-[#FFFFFF] border border-[#E5E7EB] rounded-md text-slate-900 text-sm focus:outline-none focus:border-[#E63946]" />
    </div>
  );
}
function KV({ k, v, mono, highlight }) {
  return (
    <div>
      <span className="text-[#64748B]">{k}:</span>{" "}
      <span className={`${mono ? "font-mono" : ""} ${highlight ? "text-[#E63946] font-medium" : ""}`}>{v || "-"}</span>
    </div>
  );
}
function Balance({ k, v, color }) {
  return (
    <div className="rounded p-2 bg-[#FFFFFF] border border-[#E5E7EB]">
      <div className="text-[9px] uppercase text-[#64748B]">{k}</div>
      <div className="font-mono mt-1" style={{ color: color || "white" }}>{v}</div>
    </div>
  );
}

async function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const max = 1280;
        let w = img.width, h = img.height;
        if (w > h && w > max) { h = (h * max) / w; w = max; }
        else if (h > max) { w = (w * max) / h; h = max; }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.78));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
